"use client"

import { useMemo } from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart
} from "recharts"
import { YearlyStat } from "@/lib/simulation"
import { useI18n } from "@/lib/i18n/hook"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"

interface TimeSeriesChartProps {
  data: YearlyStat[]
  totalInvestmentFn: (year: number) => number
  targetAmount?: number
}

export function TimeSeriesChart({ data, totalInvestmentFn, targetAmount }: TimeSeriesChartProps) {
  const { t } = useI18n()

  const chartData = useMemo(() => {
    return data.map((stat) => ({
      year: stat.year,
      p5: stat.percentiles.p5,
      p25: stat.percentiles.p25,
      p50: stat.percentiles.p50,
      p75: stat.percentiles.p75,
      p95: stat.percentiles.p95,
      mean: stat.mean,
      totalInvestment: totalInvestmentFn(stat.year)
    }))
  }, [data, totalInvestmentFn])

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t("riskSimulation.timeSeriesChart.title")}</CardTitle>
        <CardDescription>
          {t("riskSimulation.timeSeriesChart.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="year" 
                label={{ 
                  value: t("common.years"), 
                  position: 'insideBottomRight', 
                  offset: -10 
                }} 
              />
              <YAxis 
                tickFormatter={(value) => 
                  new Intl.NumberFormat('ja-JP', { 
                    style: 'currency', 
                    currency: 'JPY',
                    notation: 'compact',
                    maximumFractionDigits: 1
                  }).format(value)
                }
              />
              <Tooltip 
                formatter={(value: number) => 
                  new Intl.NumberFormat('ja-JP', { 
                    style: 'currency', 
                    currency: 'JPY' 
                  }).format(value)
                }
                labelFormatter={(label) => `${label}${t("common.years")}`}
              />
              <Legend />
              
              {/* Reset selection using transparent area if needed, but ComposedChart is fine */}

              {/* Range Areas (Optional: Shading between p5 and p95) */}
              
              <Line
                name={t("riskSimulation.percentiles.p95")}
                type="monotone"
                dataKey="p95"
                stroke="#10b981" // Green
                strokeWidth={2}
                dot={false}
              />
              <Line
                name={t("riskSimulation.percentiles.p50")}
                type="monotone"
                dataKey="p50"
                stroke="#3b82f6" // Blue
                strokeWidth={3}
                dot={false}
              />
              <Line
                name={t("riskSimulation.percentiles.p5")}
                type="monotone"
                dataKey="p5"
                stroke="#ef4444" // Red
                strokeWidth={2}
                dot={false}
              />
              <Line
                name={t("common.statistics.totalInvestment")}
                type="monotone"
                dataKey="totalInvestment"
                stroke="#9ca3af" // Gray
                strokeDasharray="5 5"
                strokeWidth={2}
                dot={false}
              />
              {targetAmount && targetAmount > 0 && (
                <Line
                  name={t("riskSimulation.targetAmount")}
                  type="monotone"
                  dataKey={() => targetAmount}
                  stroke="#f59e0b" // Amber
                  strokeDasharray="3 3"
                  strokeWidth={2}
                  dot={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 text-sm text-muted-foreground">
          <ul className="list-disc list-inside space-y-1">
            <li><span className="text-emerald-500 font-bold">●</span> {t("riskSimulation.percentiles.p95_desc")}</li>
            <li><span className="text-blue-500 font-bold">●</span> {t("riskSimulation.percentiles.p50_desc")}</li>
            <li><span className="text-red-500 font-bold">●</span> {t("riskSimulation.percentiles.p5_desc")}</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}
